import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { 
  ArrowLeft, Search, Download, Plus, Users, UserCheck, UserX, 
  Calendar, Trash2, Shield, X, Check, Tag, Phone, Mail
} from 'lucide-react';

const AdminContacts = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterSubscribed, setFilterSubscribed] = useState(null);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAnonymizeDialog, setShowAnonymizeDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  
  // New contact form
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    is_subscribed: true,
    reservation_source: 'phone',
    tags: []
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}` };
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [search, filterTag, filterSubscribed]);

  const fetchData = async () => {
    await Promise.all([fetchContacts(), fetchTags(), fetchStats()]);
    setLoading(false);
  };

  const fetchContacts = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (filterTag) url += `tag=${filterTag}&`;
      if (filterSubscribed !== null) url += `subscribed=${filterSubscribed}&`;

      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les contacts", variant: "destructive" });
    }
  };

  const fetchTags = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/tags`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTags(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch tags');
    }
  };

  const fetchStats = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/stats`, { headers });
      if (response.ok) {
        const data = await response.json();
        setStats(data || {});
      }
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleCreateContact = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    if (!newContact.name || !newContact.email) {
      toast({ title: "Erreur", description: "Nom et email requis", variant: "destructive" });
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(newContact)
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Contact créé" });
        setShowAddModal(false);
        setNewContact({ name: '', email: '', phone: '', is_subscribed: true, reservation_source: 'phone', tags: [] });
        fetchData();
      } else {
        const err = await response.json();
        toast({ title: "Erreur", description: err.detail || "Impossible de créer le contact", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleUpdateContact = async (contactId, updates) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/${contactId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Contact mis à jour" });
        fetchData();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleDeleteContact = async () => {
    const headers = getAuthHeader();
    if (!headers || !selectedContact) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/${selectedContact.id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Contact supprimé" });
        setShowDeleteDialog(false);
        setSelectedContact(null);
        fetchData();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleAnonymizeContact = async () => {
    const headers = getAuthHeader();
    if (!headers || !selectedContact) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/${selectedContact.id}/anonymize`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Données anonymisées (RGPD)" });
        setShowAnonymizeDialog(false);
        setSelectedContact(null);
        fetchData();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleExport = async (subscribedOnly = true) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/export/csv?subscribed_only=${subscribedOnly}`,
        { headers }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contacts_larotonde_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast({ title: "Succès", description: "Export CSV téléchargé" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur d'export", variant: "destructive" });
    }
  };

  const getTagById = (tagId) => tags.find(t => t.id === tagId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteContact}
        title="Supprimer ce contact ?"
        message="Cette action est irréversible. Le contact sera définitivement supprimé."
        confirmText="Supprimer"
        variant="danger"
      />

      {/* Anonymize Dialog */}
      <ConfirmDialog
        isOpen={showAnonymizeDialog}
        onClose={() => setShowAnonymizeDialog(false)}
        onConfirm={handleAnonymizeContact}
        title="Anonymiser ce contact (RGPD) ?"
        message="Les données personnelles seront remplacées par des données anonymes. Cette action est irréversible."
        confirmText="Anonymiser"
        variant="danger"
      />

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Contacts Newsletter</h1>
              <p className="text-sm text-gray-500">Gestion des inscriptions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => handleExport(true)}
              className="flex items-center gap-2"
            >
              <Download size={18} />
              <span className="hidden md:inline">Exporter inscrits</span>
            </Button>
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            >
              <Plus size={18} />
              <span className="hidden md:inline">Nouveau contact</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <Users className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_contacts || 0}</p>
                <p className="text-xs text-gray-500">Total contacts</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-full">
                <UserCheck className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_subscribed || 0}</p>
                <p className="text-xs text-gray-500">Inscrits</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 p-2 rounded-full">
                <UserX className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_unsubscribed || 0}</p>
                <p className="text-xs text-gray-500">Désabonnés</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-2 rounded-full">
                <Calendar className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.new_this_month || 0}</p>
                <p className="text-xs text-gray-500">Ce mois</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Tous les tags</option>
              {tags.map(tag => (
                <option key={tag.id} value={tag.id}>{tag.name}</option>
              ))}
            </select>
            <select
              value={filterSubscribed === null ? '' : filterSubscribed.toString()}
              onChange={(e) => setFilterSubscribed(e.target.value === '' ? null : e.target.value === 'true')}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Tous les statuts</option>
              <option value="true">Inscrits uniquement</option>
              <option value="false">Désabonnés uniquement</option>
            </select>
          </div>
        </div>

        {/* Contacts List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Téléphone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Tags</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Source</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      Aucun contact trouvé
                    </td>
                  </tr>
                ) : (
                  contacts.map(contact => (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {contact.is_subscribed ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Check size={12} className="mr-1" />
                            Inscrit
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <X size={12} className="mr-1" />
                            Désabonné
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{contact.name}</td>
                      <td className="px-4 py-3 text-gray-600">{contact.email}</td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{contact.phone || '-'}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {contact.tags?.map(tagId => {
                            const tag = getTagById(tagId);
                            return tag ? (
                              <span
                                key={tagId}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs"
                                style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                              >
                                {tag.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm hidden lg:table-cell">
                        {contact.reservation_source === 'website' ? 'Site web' :
                         contact.reservation_source === 'phone' ? 'Téléphone' :
                         contact.reservation_source === 'walk-in' ? 'Sur place' : contact.reservation_source}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUpdateContact(contact.id, { is_subscribed: !contact.is_subscribed })}
                            title={contact.is_subscribed ? "Désabonner" : "Réabonner"}
                          >
                            {contact.is_subscribed ? <UserX size={16} /> : <UserCheck size={16} />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedContact(contact);
                              setShowAnonymizeDialog(true);
                            }}
                            title="Anonymiser (RGPD)"
                          >
                            <Shield size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              setSelectedContact(contact);
                              setShowDeleteDialog(true);
                            }}
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination info */}
          <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500">
            {total} contact{total > 1 ? 's' : ''} au total
          </div>
        </div>

        {/* RGPD Notice */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Protection des données (RGPD) :</strong> Tous les consentements sont enregistrés avec date et source. 
            Utilisez le bouton <Shield size={14} className="inline" /> pour anonymiser un contact sur demande.
          </p>
        </div>
      </main>

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Nouveau contact</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Nom *</Label>
                <Input
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>
              
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="jean@example.com"
                />
              </div>
              
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  placeholder="06 12 34 56 78"
                />
              </div>
              
              <div>
                <Label>Source</Label>
                <select
                  value={newContact.reservation_source}
                  onChange={(e) => setNewContact({ ...newContact, reservation_source: e.target.value })}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="phone">Réservation téléphone</option>
                  <option value="walk-in">Sur place</option>
                  <option value="website">Site web</option>
                </select>
              </div>
              
              <div>
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => {
                        const newTags = newContact.tags.includes(tag.id)
                          ? newContact.tags.filter(t => t !== tag.id)
                          : [...newContact.tags, tag.id];
                        setNewContact({ ...newContact, tags: newTags });
                      }}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        newContact.tags.includes(tag.id)
                          ? 'border-transparent text-white'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                      style={newContact.tags.includes(tag.id) ? { backgroundColor: tag.color } : {}}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Switch
                  checked={newContact.is_subscribed}
                  onCheckedChange={(v) => setNewContact({ ...newContact, is_subscribed: v })}
                />
                <Label>Inscrit à la newsletter</Label>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateContact} className="bg-green-600 hover:bg-green-700">
                Créer le contact
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
